import { createApp } from "vue";
import { SubscriptionCallbackMutation, createPinia } from "pinia";
import piniaPersist from "pinia-plugin-persist";
import VueEasyLightbox from "vue-easy-lightbox";
import { setupI18n, loadLanguageAsync } from "@/i18n";
import { Field, Form, ErrorMessage, configure, defineRule } from "vee-validate";
import { localize } from "@vee-validate/i18n";
import { confirmed, email, regex, required } from "@vee-validate/rules";
import sv from "@vee-validate/i18n/dist/locale/sv.json";
import da from "@vee-validate/i18n/dist/locale/da.json";
import no from "@vee-validate/i18n/dist/locale/nb_NO.json";
import UmbracoForm from "@/components/umbraco-forms/structure/UmbracoForm.vue";
import UmbracoFormPage from "@/components/umbraco-forms/structure/UmbracoFormPage.vue";
import UmbracoFormField from "@/components/umbraco-forms/structure/UmbracoFormField.vue";
import UmbracoFormFieldset from "@/components/umbraco-forms/structure/UmbracoFormFieldset.vue";
import UmbracoFormFieldsetColumn from "@/components/umbraco-forms/structure/UmbracoFormFieldsetColumn.vue";
import TressTheCheckout from "@/pages/TressTheCheckout.vue";
import TressSearchInput from "@/components/header/TressSearchInput.vue";
import TressProductListings from "@/components/TressProductListings.vue";
import TressImageCarousel from "@/components/TressImageCarousel.vue";
import TressCarouselControls from "@/components/TressCarouselControls.vue";
import TressButton from "@/components/TressButton.vue";
import TressCaseListing from "@/components/TressCaseListing.vue";
import TressLightbox from "@/components/TressLightbox.vue";
import TressDivider2 from "@/components/TressDivider2.vue";
import TressIconButton from "@/components/TressIconButton.vue";
import TressSwitchButton from "@/components/TressSwitchButton.vue";
import TressCloseButton from "@/components/TressCloseButton.vue";
import TressContentTile from "@/components/TressContentTile.vue";
import TressSearchPopup from "@/components/TressSearchPopup.vue";
import TressSearchWrapper from "@/components/FunctionWrappers/TressSearchWrapper.vue";
import BigBannerWrapper from "@/components/FunctionWrappers/BigBannerBlockWrapper.vue";
import HeaderWrapper from "@/components/FunctionWrappers/HeaderWrapper.vue";
import FooterWrapper from "@/components/FunctionWrappers/FooterWrapper.vue";
import CategoryWrapper from "@/components/FunctionWrappers/CategoryWrapper.vue";
import ProductWrapper from "@/components/FunctionWrappers/ProductWrapper.vue";
import TabsBlockWrapper from "@/components/FunctionWrappers/TabsBlockWrapper.vue";
import ProductListWrapper from "@/components/FunctionWrappers/ProductListWrapper.vue";
import CasePageWrapper from "@/components/FunctionWrappers/CasePageWrapper.vue";
import InspirationBlockWrapper from "@/components/FunctionWrappers/InspirationBlockWrapper.vue";
import ProductTileWrapper from "@/components/FunctionWrappers/ProductTileWrapper.vue";
import SearchPageWrapper from "@/components/FunctionWrappers/TressSearchPageWrapper.vue";
import AllCategoriesBlockWrapper from "@/components/FunctionWrappers/AllCategoriesBlockWrapper.vue";
import LinkBoxBlockWrapper from "@/components/FunctionWrappers/LinkBoxWrapper.vue";
import ContactSalesPersonWrapper from "@/components/FunctionWrappers/ContactSalesPersonBlockWrapper.vue";
import FilterBlockWrapper from "@/components/FunctionWrappers/FilterBlockWrapper.vue";
import SalesWrapper from "@/components/FunctionWrappers/SalesmanWrapper.vue";
import TressLightboxWrapper from "@/components/FunctionWrappers/TressLightboxWrapper.vue";
import FaqBlockWrapper from "@/components/FunctionWrappers/FaqBlockWrapper.vue";
import SalesInfo from "@/components/TressSalesmanInfo.vue";
import TressModal from "@/components/TressModal.vue";
import TressCaseOverview from "@/components/FunctionWrappers/CaseOverviewWrapper.vue";
import IconArrowLeft from "@/components/icons/IconArrowLeft.vue";
import IconArrowRight from "@/components/icons/IconArrowRight.vue";
import IconCart from "@/components/icons/IconCart.vue";
import IconHome from "@/components/icons/IconHome.vue";
import IconChevronDown from "@/components/icons/IconChevronDown.vue";
import IconChevronRight from "@/components/icons/IconChevronRight.vue";
import IconClose from "@/components/icons/IconClose.vue";
import IconCloseThin from "@/components/icons/IconCloseThin.vue";
import IconLinkedIn from "@/components/icons/IconLinkedIn.vue";
import IconDownload from "@/components/icons/IconDownload.vue";
import IconFavorite from "@/components/icons/IconFavorite.vue";
import IconHamburgerMenu from "@/components/icons/IconHamburgerMenu.vue";
import IconSearch from "@/components/icons/IconSearch.vue";
import IconUser from "@/components/icons/IconUser.vue";
import IconSliders from "@/components/icons/IconSliders.vue";
import IconCurvedArrowRight from "@/components/icons/IconCurvedArrowRight.vue";
import IconCurvedArrowRight2 from "@/components/icons/IconCurvedArrowRight2.vue";
import IconCurvedArrowDown from "@/components/icons/IconCurvedArrowDown.vue";
import IconMulticolor from "@/components/icons/IconMultiColor.vue";
import IconTransparent from "@/components/icons/IconTransparent.vue";
import IconContact from "@/components/icons/IconContact.vue";
import IconRal from "@/components/icons/IconRal.vue";
import IconMail from "@/components/icons/IconMail.vue";
import IconMap from "@/components/icons/IconMap.vue";
import IconCaseLarge from "@/components/icons/IconCaseLarge.vue";
import IconCasePageCity from "@/components/icons/IconCasePageCity.vue";
import IconCasePageProductCategories from "@/components/icons/IconCasePageProductCategories.vue";
import IconCasePageBuildYear from "@/components/icons/IconCasePageBuildYear.vue";
import IconCasePageActivities from "@/components/icons/IconCasePageActivities.vue";
import IconCheckmark from "@/components/icons/IconCheckmark.vue";
import IconWood from "@/components/icons/IconWood.vue";
import IconTrashBin from "@/components/icons/IconTrashBin.vue";
import IconRemove from "@/components/icons/IconRemove.vue";
import IconAdd from "@/components/icons/IconAdd.vue";
import IconAddLarge from "@/components/icons/IconAddLarge.vue";
import IconMinus from "@/components/icons/IconMinus.vue";
import ImageDefaultProductTile from "@/components/images/ImageDefaultProductTile.vue";
import TressScroll from "@/components/TressScroll.vue";
import TressSpecialAccessoriesModal from "@/components/TressSpecialAccessoriesModal.vue";
import TressSpecialAccessoriesModalView from "@/components/TressSpecialAccessoriesModalView.vue";
import TressVideoPlayer from "@/components/TressVideoPlayer.vue";
import TressVideoPlayer2 from "@/components/TressVideoPlayer2.vue";
import TressPreviewPlayer from "@/components/TressPreviewPlayer.vue";
import TressPagination from "./components/TressPagination.vue";
import TressPaginationAnnouncer from "./components/TressPaginationAnnouncer.vue";
import TressGoogleMap from "@/components/TressGoogleMap.vue";
import TressCartPopUp from "@/components/TressCartPopUp.vue";
import TressBuyButton from "@/components/TressBuyButton.vue";
import TressBuyButtonIcon from "@/components/TressBuyButtonIcon.vue";
import TressProductTile from "@/components/TressProductTile.vue";
import TressLoginModal from "@/components/TressLoginModal.vue";
import "spin.js/spin.css";
import "sweetalert2/dist/sweetalert2.css";
import TressAccountResetPasswordForm from "@/components/account/TressAccountResetPasswordForm.vue";
import TressTheSignIn from "@/pages/TressTheSignIn.vue";
import TressTheMyPagesChangePassword from "@/pages/TressTheMyPagesChangePassword.vue";
import TressTheMyPagesProfile from "@/pages/TressTheMyPagesProfile.vue";
import TressTheMyPagesFavorites from "@/pages/TressTheMyPagesFavorites.vue";
import TressTheMyPagesOrderHistory from "@/pages/TressTheMyPagesOrderHistory.vue";
import { emitter } from "@/services/Emitter";
import ContentOverviewPageWrapper from "@/components/FunctionWrappers/ContentOverviewPageWrapper.vue";
import { useCart } from "./store/useCart";
import { useChosenSalesman } from "@/store/useChosenSalesman";
import { useCheckout } from "@/store/useCheckout";
import TressProductRecommendations from "@/components/TressProductRecommendations.vue";
import { MutationType } from "pinia";
import { Cart } from "./models/Cart";
import { MessageSet } from "./i18n/MessageSet";
import IconAnimatedPlusMinus from "./components/icons/IconAnimatedPlusMinus.vue";
import TressDropdown from "@/components/filters/TressDropdown.vue";
import TressCheckboxList from "@/components/filters/TressCheckboxList.vue";
import TressFrame from "@/components/filters/TressFrame.vue";
import TressFilter from "@/components/filters/TressFilter.vue";
import TressMobileFilter from "@/components/filters/TressMobileFilter.vue";
import TressAccordion from "@/components/filters/TressAccordion/TressAccordion.vue";
import TressAccordionItem from "@/components/filters/TressAccordion/TressAccordionItem.vue";

const lang = (document.querySelector("html") as HTMLHtmlElement).getAttribute("lang") as string;

defineRule("confirmed", confirmed);
defineRule("email", email);
defineRule("regex", regex);
defineRule("required", required);

const i18n = setupI18n({
  globalInjection: true,
  legacy: false,
  locale: lang,
  fallbackLocale: "en",
});

const store = createPinia();
store.use(piniaPersist);

const veeValidateDa: MessageSet = { ...da };
veeValidateDa.messages.confirmed = "{field} skal matche";
const veeValidateSv: MessageSet = { ...sv };
veeValidateSv.messages.confirmed = "Fältet {field} matchar inte";
const veeValidateNo: MessageSet = { ...no };
// https://vee-validate.logaretm.com/v4/guide/i18n#overview

configure({
  generateMessage: localize({ sv: veeValidateSv, nb: veeValidateNo, da: veeValidateDa }),
});

loadLanguageAsync(i18n, lang, window.translations);

const app = createApp({})
  .use(VueEasyLightbox)
  .use(i18n)
  .use(store)
  .provide("eventbus", emitter)
  .provide("tressSite", window.tressSite)
  .component("icon-arrow-left", IconArrowLeft)
  .component("icon-arrow-right", IconArrowRight)
  .component("icon-cart", IconCart)
  .component("icon-case-large", IconCaseLarge)
  .component("icon-case-page-activities", IconCasePageActivities)
  .component("icon-case-page-build-year", IconCasePageBuildYear)
  .component("icon-case-page-city", IconCasePageCity)
  .component("icon-case-page-product-categories", IconCasePageProductCategories)
  .component("icon-checkmark", IconCheckmark)
  .component("icon-chevron-down", IconChevronDown)
  .component("icon-chevron-right", IconChevronRight)
  .component("icon-close", IconClose)
  .component("icon-close-thin", IconCloseThin)
  .component("icon-contact", IconContact)
  .component("icon-curved-arrow-down", IconCurvedArrowDown)
  .component("icon-curved-arrow-right", IconCurvedArrowRight)
  .component("icon-curved-arrow-right2", IconCurvedArrowRight2)
  .component("icon-download", IconDownload)
  .component("icon-favorite", IconFavorite)
  .component("icon-hamburger-menu", IconHamburgerMenu)
  .component("icon-home", IconHome)
  .component("icon-linked-in", IconLinkedIn)
  .component("icon-mail", IconMail)
  .component("icon-map", IconMap)
  .component("icon-multicolor", IconMulticolor)
  .component("icon-ral", IconRal)
  .component("icon-search", IconSearch)
  .component("icon-sliders", IconSliders)
  .component("icon-transparent", IconTransparent)
  .component("icon-user", IconUser)
  .component("icon-wood", IconWood)
  .component("icon-trash-bin", IconTrashBin)
  .component("icon-remove", IconRemove)
  .component("icon-add", IconAdd)
  .component("icon-minus", IconMinus)
  .component("icon-add-large", IconAddLarge)
  .component("icon-animated-plus-minus", IconAnimatedPlusMinus)
  .component("tress-all-categories-block-wrapper", AllCategoriesBlockWrapper)
  .component("tress-accordion", TressAccordion)
  .component("tress-accordion-item", TressAccordionItem)
  .component("tress-big-banner-block-wrapper", BigBannerWrapper)
  .component("tress-button", TressButton)
  .component("tress-buy-button-icon", TressBuyButtonIcon)
  .component("tress-buy-button", TressBuyButton)
  .component("tress-carousel-controls", TressCarouselControls)
  .component("tress-cart-pop-up", TressCartPopUp)
  .component("tress-case-listing", TressCaseListing)
  .component("tress-case-overview", TressCaseOverview)
  .component("tress-case-page-wrapper", CasePageWrapper)
  .component("tress-category-wrapper", CategoryWrapper)
  .component("tress-checkbox-list", TressCheckboxList)
  .component("tress-close-button", TressCloseButton)
  .component("tress-contact-sales-person-block-wrapper", ContactSalesPersonWrapper)
  .component("tress-content-overview-page-wrapper", ContentOverviewPageWrapper)
  .component("tress-content-tile", TressContentTile)
  .component("tress-divider2", TressDivider2)
  .component("tress-dropdown", TressDropdown)
  .component("tress-faq-block-wrapper", FaqBlockWrapper)
  .component("tress-filter", TressFilter)
  .component("tress-mobile-filter", TressMobileFilter)
  .component("tress-frame", TressFrame)
  .component("tress-filter-block-wrapper", FilterBlockWrapper)
  .component("tress-footer-wrapper", FooterWrapper)
  .component("tress-google-map", TressGoogleMap)
  .component("tress-header-wrapper", HeaderWrapper)
  .component("tress-icon-button", TressIconButton)
  .component("tress-image-carousel", TressImageCarousel)
  .component("tress-inspiration-block-wrapper", InspirationBlockWrapper)
  .component("tress-lightbox-wrapper", TressLightboxWrapper)
  .component("tress-lightbox", TressLightbox)
  .component("tress-link-box-block-wrapper", LinkBoxBlockWrapper)
  .component("tress-login-modal", TressLoginModal)
  .component("tress-modal", TressModal)
  .component("tress-preview-player", TressPreviewPlayer)
  .component("tress-pagination", TressPagination)
  .component("tress-pagination-announcer", TressPaginationAnnouncer)
  .component("tress-product-list-wrapper", ProductListWrapper)
  .component("tress-product-listings", TressProductListings)
  .component("tress-product-recommendations", TressProductRecommendations)
  .component("tress-product-tile-default-image", ImageDefaultProductTile)
  .component("tress-product-tile-wrapper", ProductTileWrapper)
  .component("tress-product-tile", TressProductTile)
  .component("tress-product-wrapper", ProductWrapper)
  .component("tress-reset-password-form", TressAccountResetPasswordForm)
  .component("tress-salesman-info", SalesInfo)
  .component("tress-salesman-wrapper", SalesWrapper)
  .component("tress-scroll", TressScroll)
  .component("tress-search-input", TressSearchInput)
  .component("tress-search-page-wrapper", SearchPageWrapper)
  .component("tress-search-popup", TressSearchPopup)
  .component("tress-search-wrapper", TressSearchWrapper)
  .component("tress-special-accessories-modal", TressSpecialAccessoriesModal)
  .component("tress-special-accessories-modal-view", TressSpecialAccessoriesModalView)
  .component("tress-switch-button", TressSwitchButton)
  .component("tress-tabs-block-wrapper", TabsBlockWrapper)
  .component("tress-the-checkout", TressTheCheckout)
  .component("tress-the-my-pages-change-password", TressTheMyPagesChangePassword)
  .component("tress-the-my-pages-favorites", TressTheMyPagesFavorites)
  .component("tress-the-my-pages-order-history", TressTheMyPagesOrderHistory)
  .component("tress-the-my-pages-profile", TressTheMyPagesProfile)
  .component("tress-the-signin", TressTheSignIn)
  .component("tress-video-player", TressVideoPlayer)
  .component("tress-video-player2", TressVideoPlayer2)
  .component("umbraco-form-field", UmbracoFormField)
  .component("umbraco-form-fieldset-column", UmbracoFormFieldsetColumn)
  .component("umbraco-form-fieldset", UmbracoFormFieldset)
  .component("umbraco-form-page", UmbracoFormPage)
  .component("umbraco-form", UmbracoForm)
  .component("v-field", Field)
  .component("v-form", Form)
  .component("v-message", ErrorMessage);

app.mount("#app");

const cart = useCart();
const checkoutStore = useCheckout();
cart.$subscribe((mutation: SubscriptionCallbackMutation<Cart>) => {
  if (mutation.type === MutationType.patchObject && mutation.payload.id === null) {
    checkoutStore.$reset();
  }
});

cart.load();
const chosenSalesman = useChosenSalesman();
chosenSalesman.load();
